import axios from "axios";

/**
 * Resolve API base URL — robust across:
 *  - Local dev (frontend :3000 + backend :8001 on different origins)
 *  - Emergent preview/prod (xxx.emergent.host)
 *  - Custom domains (e.g. agent.athlynx.co)
 *
 * Strategy:
 *  - If running in a browser on a host that DOES NOT match the build-time
 *    REACT_APP_BACKEND_URL host (= we're on a custom domain), use same-origin
 *    so the Emergent ingress routes /api to the backend on the SAME domain.
 *    This makes cookies work natively (no cross-site issues) on Safari/Chrome.
 *  - Otherwise use the build-time URL (preserves dev/preview behaviour).
 */
function resolveBase() {
  const buildUrl = process.env.REACT_APP_BACKEND_URL || "";
  if (typeof window === "undefined") return buildUrl;

  const currentOrigin = window.location.origin;
  if (!buildUrl) return currentOrigin;

  try {
    const u = new URL(buildUrl);
    if (u.origin !== currentOrigin) {
      // Custom domain or different deployment — use same-origin
      return currentOrigin;
    }
  } catch (_) { /* fall through */ }
  return buildUrl;
}

const BASE = resolveBase();
export const API = `${BASE}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Global 402 (subscription required) → dispatch event the App listens to
api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 402) {
      const detail = error.response.data?.detail;
      const msg = typeof detail === "object" ? detail?.message : detail;
      try {
        window.dispatchEvent(new CustomEvent("athlynx:paywall", { detail: { message: msg } }));
      } catch (_) { /* best effort */ }
    }
    return Promise.reject(error);
  }
);

export default api;
